import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Card from "@/v2-ui/card"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"

export type UsageReportCardProps = Omit<
  ComponentProps<typeof Card>,
  "title"
> & {
  /** Short title inside the card ("Most used tools"). */
  title?: ReactNode
  /** Longer methodology behind a small info icon next to the title. */
  info?: string
  /** Controls on the title row's right. */
  aside?: ReactNode
  /**
   * One line under the content that says what the numbers are ("The 15
   * most used tools, calls from customers"), the report's figure caption.
   */
  caption?: ReactNode
  /** Amber edge for a card whose content is a warning (refusals). */
  tone?: "default" | "warning"
  /** Span the whole grid row. */
  wide?: boolean
}

/**
 * The grouping surface: one topic per card, on the Backoffice card
 * (`Card`: rounded, bordered, `bg-card`), with an optional title row and a
 * caption. Cards sit in a grid under a section header and give every list,
 * chart and table its own frame, so related numbers read as one thing and
 * unrelated ones stay apart.
 */
function UsageReportCard(props: UsageReportCardProps) {
  const {
    title,
    info,
    aside,
    caption,
    tone = "default",
    wide = false,
    className,
    children,
    ...cardProps
  } = props

  return (
    <Card
      className={cn(
        "usage-report-card",
        tone === "warning" && "usage-report-card--warning",
        wide && "usage-report-card--wide",
        className,
      )}
      {...cardProps}
    >
      {(title != null || aside != null) && (
        <div className="usage-report-card-header">
          {title != null && (
            <h4 className="usage-report-card-title">{title}</h4>
          )}
          {aside != null && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {aside}
            </div>
          )}
        </div>
      )}
      {children}
      {caption != null && (
        <p className="usage-report-card-caption">{caption}</p>
      )}
    </Card>
  )
}

export default UsageReportCard
